/**
 * Trigger editor - pairs an event source (workflow event / workflow action /
 * API call) with an EmailContentTemplate, optionally narrowed by a predicate
 * authored via the shared ConditionBuilder.
 *
 * Audience is tenant admins, so the labels speak about "when this happens"
 * rather than DSL primitives, and the predicate JSON is hidden behind the
 * builder.
 */

import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ArrowLeft, ChevronRight, Trash2, Zap } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Separator } from '@/shared/components/ui/separator'

import { ConditionBuilder } from '@/admin/components/condition-builder'
import type { PredicateNode } from '@/admin/components/condition-builder'
import { useAvailableRefs } from '@/admin/components/condition-builder/use-available-refs'

import { useEmailTemplateList } from '@/admin/pages/settings/email-authoring/use-email-templates'
import { useWorkflowDefinitions } from '@/admin/hooks/use-workflow-definitions'

import {
  useCreateTrigger,
  useDeleteTrigger,
  useEmailTrigger,
  useTriggerKeys,
  useUpdateTrigger,
} from './use-email-triggers'
import type {
  RecipientKind,
  RecipientSpec,
  SendMode,
  TriggerDetail,
  TriggerKind,
} from './types'

const BLANK_TRIGGER: TriggerDetail = {
  id: 0,
  uuid: '',
  trigger_kind: 'workflow_event',
  trigger_key: '',
  template: 0,
  template_slug: '',
  template_name: '',
  template_kind: 'notification',
  workflow: null,
  workflow_uuid: null,
  workflow_label: '',
  predicate: {},
  send_mode: 'auto',
  priority: 100,
  cooldown_seconds: 60,
  is_active: true,
  key_is_resolvable: true,
  recipients: [{ kind: 'consumer' }],
  updated_at: '',
}

/**
 * Default field values per recipient kind - used when the admin toggles
 * a kind ON. ``consumer`` / ``contractor`` carry no ``from`` field; the
 * dotted path to reach those records is platform-internal and resolved
 * at dispatch time via the resource adapter. ``third_party`` still
 * needs the admin to name the captured-values keys.
 */
const RECIPIENT_DEFAULTS: Record<RecipientKind, RecipientSpec> = {
  consumer: { kind: 'consumer' },
  contractor: { kind: 'contractor' },
  third_party: { kind: 'third_party', email_from_captured: '' },
  collaborators: { kind: 'collaborators' },
}

const RECIPIENT_OPTIONS: Array<{
  value: RecipientKind
  label: string
  description: string
}> = [
  {
    value: 'consumer',
    label: 'Consumer',
    description: 'Send to the consumer record attached to the resource.',
  },
  {
    value: 'contractor',
    label: 'Contractor',
    description: 'Send to the contractor (installer / repairer) on the resource.',
  },
  {
    value: 'third_party',
    label: 'Third party',
    description:
      'Send to an email captured at action time (e.g. a lab, an external partner).',
  },
  {
    value: 'collaborators',
    label: 'Collaborators',
    description:
      "Send to the claim's external collaborator contacts.",
  },
]

const KIND_OPTIONS: Array<{
  value: TriggerKind
  label: string
  description: string
}> = [
  {
    value: 'workflow_event',
    label: 'When something happens in a workflow',
    description: 'e.g. a claim is created, a registration is approved.',
  },
  {
    value: 'workflow_action',
    label: 'When a user takes a workflow action',
    description: 'e.g. an agent presses "Request more info" or "Reject".',
  },
  {
    value: 'api',
    label: 'When something calls our API',
    description: 'e.g. an integration posts /messages/send.',
  },
]

const SEND_MODE_OPTIONS: Array<{
  value: SendMode
  label: string
  description: string
}> = [
  {
    value: 'auto',
    label: 'Send automatically',
    description: 'The email is dispatched as soon as the trigger fires.',
  },
  {
    value: 'review_first',
    label: 'Review before sending',
    description: 'A draft is queued for an agent to review and send.',
  },
]

export default function EmailTriggerEditor() {
  const { uuid } = useParams<{ uuid?: string }>()
  const isNew = uuid === 'new'
  const navigate = useNavigate()

  const { data: existing, isLoading: loadingExisting } = useEmailTrigger(
    isNew ? undefined : uuid,
  )
  const { data: templates = [], isLoading: loadingTemplates } =
    useEmailTemplateList('all')
  const { data: workflows = [], isLoading: loadingWorkflows } =
    useWorkflowDefinitions()

  const createMutation = useCreateTrigger()
  const updateMutation = useUpdateTrigger(uuid ?? '')
  const deleteMutation = useDeleteTrigger()

  const [draft, setDraft] = useState<Partial<TriggerDetail>>({})
  const [deleteOpen, setDeleteOpen] = useState(false)

  const form: TriggerDetail | null = useMemo(() => {
    if (isNew) return { ...BLANK_TRIGGER, ...draft }
    if (!existing) return null
    return { ...existing, ...draft }
  }, [isNew, existing, draft])

  // Keys + predicate refs scope to the in-progress form even before the
  // early returns below, so the hooks fire in a stable order.
  const formKind = form?.trigger_kind ?? 'workflow_event'
  const formWorkflowUuid = form?.workflow ?? null
  const selectedWorkflow = workflows.find((w) => w.uuid === formWorkflowUuid)
  const { data: keySuggestions = [], isLoading: loadingKeys } = useTriggerKeys(
    formKind,
    formWorkflowUuid,
  )
  const {
    refs,
    byRef,
    isLoading: loadingRefs,
  } = useAvailableRefs({
    entityKey: selectedWorkflow?.resource_type || undefined,
  })

  if (!form) return <p className="text-sm text-slate-500">Loading…</p>
  if (!isNew && loadingExisting)
    return <p className="text-sm text-slate-500">Loading…</p>

  const update = (patch: Partial<TriggerDetail>) =>
    setDraft((d) => ({ ...d, ...patch }))

  const selectedTemplate = templates.find((t) => t.id === form.template)

  // api triggers have a single registered key; the rest get the dropdown.
  const autoKeyForApi = form.trigger_kind === 'api'

  // For api kind, derive the trigger key from the (single) published
  // suggestion when the form hasn't stored one yet. Computed during
  // render rather than via a useEffect+setState - keeps the form state
  // pure and avoids the cascading-renders smell.
  const resolvedTriggerKey =
    autoKeyForApi && !form.trigger_key && keySuggestions[0]
      ? keySuggestions[0].key
      : form.trigger_key

  const keyIsKnown = keySuggestions.some((s) => s.key === resolvedTriggerKey)

  const predicateValue: PredicateNode | null =
    form.predicate && Object.keys(form.predicate).length > 0
      ? (form.predicate as PredicateNode)
      : null

  const canSave =
    !!form.template &&
    !!resolvedTriggerKey.trim() &&
    !!form.trigger_kind

  const handleSave = async () => {
    if (!canSave) {
      toast.error('Pick an event, a template, and the source first.')
      return
    }
    try {
      const payload = {
        template: form.template,
        trigger_kind: form.trigger_kind,
        trigger_key: resolvedTriggerKey.trim(),
        workflow: form.workflow,
        predicate: form.predicate,
        send_mode: form.send_mode,
        priority: form.priority,
        cooldown_seconds: form.cooldown_seconds,
        is_active: form.is_active,
        recipients: form.recipients,
      }
      if (isNew) {
        const created = await createMutation.mutateAsync(payload)
        toast.success('Trigger created.')
        navigate(`/settings/email-triggers/${created.uuid}`, { replace: true })
      } else {
        await updateMutation.mutateAsync(payload)
        toast.success('Trigger saved.')
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 400) {
        toast.error('Could not save - check conditions and selected template.')
      } else {
        toast.error("Couldn't save. Try again.")
      }
    }
  }

  const handleDelete = async () => {
    if (isNew || !existing) return
    try {
      await deleteMutation.mutateAsync({ uuid: existing.uuid })
      toast.success('Trigger deleted.')
      navigate('/settings/email-triggers')
    } catch {
      toast.error("Couldn't delete trigger.")
    }
  }

  return (
    <div className="space-y-5 pb-12">
      <Header
        form={form}
        isNew={isNew}
        onBack={() => navigate('/settings/email-triggers')}
        onSave={handleSave}
        onDelete={() => setDeleteOpen(true)}
        saving={createMutation.isPending || updateMutation.isPending}
      />

      {/* Step 1 - when */}
      <section className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-semibold text-white">
            1
          </span>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            When should this fire?
          </h2>
        </div>

        <div className="space-y-2">
          {KIND_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                update({
                  trigger_kind: opt.value,
                  trigger_key: '',
                  workflow: opt.value === 'api' ? null : form.workflow,
                })
              }
              className={
                'w-full text-left rounded-lg border px-4 py-3 transition-colors ' +
                (form.trigger_kind === opt.value
                  ? 'border-slate-900 bg-slate-50'
                  : 'border-slate-200 hover:border-slate-300')
              }
            >
              <div className="font-medium text-sm text-slate-900">{opt.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{opt.description}</div>
            </button>
          ))}
        </div>

        {/* Workflow picker - only for workflow-sourced triggers */}
        {form.trigger_kind !== 'api' && (
          <div>
            <Label>Which workflow?</Label>
            <Select
              value={form.workflow ?? ''}
              onValueChange={(v: string) =>
                update({ workflow: v || null, trigger_key: '' })
              }
            >
              <SelectTrigger className="mt-1">
                <SelectValue
                  placeholder={
                    loadingWorkflows ? 'Loading workflows…' : 'Any workflow'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {workflows.map((w) => (
                  <SelectItem key={w.uuid} value={w.uuid}>
                    {w.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              Scopes the event list below. Pick the workflow whose states or
              actions should drive this email.
            </p>
          </div>
        )}

        {/* Key picker - registry-driven. For API triggers, the published
            key is auto-assumed; we show a confirmation row instead of a
            one-option dropdown. */}
        {autoKeyForApi ? (
          <div>
            <Label>API event</Label>
            <div className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              {loadingKeys ? (
                <span className="text-sm text-slate-500">Loading…</span>
              ) : keySuggestions.length === 0 ? (
                <span className="text-sm text-rose-700">
                  No API events registered. Reach out to engineering.
                </span>
              ) : (
                <span className="text-sm text-slate-900">
                  {keySuggestions[0].label}
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Wires this trigger to the public API endpoint. Anyone with a
              valid API key for your tenant can fire it.
            </p>
          </div>
        ) : (
          <div>
            <Label>
              {form.trigger_kind === 'workflow_event'
                ? 'Which state?'
                : 'Which action?'}
            </Label>
            <Select
              value={form.trigger_key}
              onValueChange={(v: string) => update({ trigger_key: v })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue
                  placeholder={
                    loadingKeys
                      ? 'Loading…'
                      : keySuggestions.length === 0
                        ? 'No states/actions found for this workflow'
                        : 'Pick an event…'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {keySuggestions.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.trigger_key && !keyIsKnown && !loadingKeys && (
              <p className="text-xs text-amber-700 mt-1">
                ⚠ The saved key{' '}
                <span className="font-mono">{form.trigger_key}</span> isn't
                in the current workflow. Pick another or re-select the workflow.
              </p>
            )}
          </div>
        )}
      </section>

      {/* Step 2 - send what */}
      <section className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-semibold text-white">
            2
          </span>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            Which email should go out?
          </h2>
        </div>

        <div>
          <Label>Template</Label>
          <Select
            value={form.template ? String(form.template) : ''}
            onValueChange={(v: string) => update({ template: Number(v) })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder={loadingTemplates ? 'Loading…' : 'Pick a template…'} />
            </SelectTrigger>
            <SelectContent>
              {templates
                .filter((t) => t.is_active)
                .map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {selectedTemplate && (
            <p className="text-xs text-slate-500 mt-1">
              Sending kind:{' '}
              <span className="font-medium">{selectedTemplate.kind}</span>. Subject:{' '}
              <span className="font-mono">{selectedTemplate.subject}</span>
            </p>
          )}
        </div>
      </section>

      {/* Step 3 - who */}
      <section className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-semibold text-white">
            3
          </span>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            Who is this email to?
          </h2>
        </div>

        <p className="text-xs text-slate-500">
          Pick any combination - the same email goes to every checked audience
          (predicate still gates whether it fires).
        </p>

        <div className="grid gap-2 md:grid-cols-3">
          {RECIPIENT_OPTIONS.map((opt) => {
            const existing = form.recipients.find((r) => r.kind === opt.value)
            const checked = !!existing
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  const next = checked
                    ? form.recipients.filter((r) => r.kind !== opt.value)
                    : [...form.recipients, RECIPIENT_DEFAULTS[opt.value]]
                  update({ recipients: next })
                }}
                className={`text-left rounded-lg border p-3 transition-all relative ${
                  checked
                    ? 'border-slate-900 bg-slate-50'
                    : 'border-slate-200 hover:border-slate-400'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span
                    className={`mt-0.5 h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                      checked
                        ? 'bg-slate-900 border-slate-900'
                        : 'border-slate-300'
                    }`}
                  >
                    {checked && (
                      <svg
                        viewBox="0 0 14 14"
                        className="h-2.5 w-2.5 text-white"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <path d="M2 7l3 3 7-7" />
                      </svg>
                    )}
                  </span>
                  <div>
                    <div className="text-sm font-medium text-slate-900">
                      {opt.label}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {opt.description}
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        {form.recipients.map((spec, index) => (
          <RecipientFields
            key={spec.kind + index}
            spec={spec}
            onChange={(patch) => {
              const next = form.recipients.map((r, i) =>
                i === index ? { ...r, ...patch } : r,
              )
              update({ recipients: next })
            }}
          />
        ))}

      </section>

      {/* Step 4 - only when */}
      <section className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-semibold text-white">
            4
          </span>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            Only when these conditions match (optional)
          </h2>
        </div>
        <p className="text-xs text-slate-500">
          Leave this empty to fire on every matching event. Add conditions to send a
          different template based on the customer, the warranty, or anything else
          in the record.
        </p>
        {loadingRefs ? (
          <p className="text-sm text-slate-500">Loading available fields…</p>
        ) : (
          <ConditionBuilder
            value={predicateValue}
            onChange={(next) => update({ predicate: next ?? {} })}
            availableRefs={refs}
            byRef={byRef}
          />
        )}
      </section>

      {/* Step 5 - how */}
      <section className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-semibold text-white">
            5
          </span>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            How should it send?
          </h2>
        </div>
        <div className="space-y-2">
          {SEND_MODE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => update({ send_mode: opt.value })}
              className={
                'w-full text-left rounded-lg border px-4 py-3 transition-colors ' +
                (form.send_mode === opt.value
                  ? 'border-slate-900 bg-slate-50'
                  : 'border-slate-200 hover:border-slate-300')
              }
            >
              <div className="font-medium text-sm text-slate-900">{opt.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{opt.description}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Advanced */}
      <Advanced
        form={form}
        update={update}
      />

      {/* Confirmation dialog - replaces native confirm() per the shared
          UI rules: destructive actions go through the Dialog primitive. */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this trigger?</DialogTitle>
            <DialogDescription>
              {form.template_name ? (
                <>
                  The <span className="font-medium">{form.template_name}</span>{' '}
                  email will stop being sent when this trigger fires.
                </>
              ) : (
                'The matching email will stop being sent when this trigger fires.'
              )}{' '}
              In-flight upload sessions minted from past sends are
              unaffected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete trigger'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Header({
  form,
  isNew,
  onBack,
  onSave,
  onDelete,
  saving,
}: {
  form: TriggerDetail
  isNew: boolean
  onBack: () => void
  onSave: () => void
  onDelete: () => void
  saving: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 min-w-0">
        <Button variant="ghost" size="icon-sm" onClick={onBack} aria-label="Back">
          <ArrowLeft size={16} />
        </Button>
        <Zap size={18} className="text-slate-500" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-base font-semibold text-slate-900 truncate">
              {isNew ? 'New trigger' : form.trigger_key || 'Trigger'}
            </h1>
            <ChevronRight size={14} className="text-slate-300" />
            <span className="text-sm text-slate-500 truncate">
              {form.template_name || 'Pick a template'}
            </span>
          </div>
          {!isNew && (
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant={form.is_active ? 'default' : 'outline'}>
                {form.is_active ? 'Active' : 'Disabled'}
              </Badge>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!isNew && (
          <Button variant="outline" size="sm" onClick={onDelete}>
            <Trash2 size={14} />
            Delete
          </Button>
        )}
        <Button onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create trigger' : 'Save changes'}
        </Button>
      </div>
    </div>
  )
}

function Advanced({
  form,
  update,
}: {
  form: TriggerDetail
  update: (patch: Partial<TriggerDetail>) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 md:p-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full"
      >
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
          Advanced
        </h2>
        <ChevronRight
          size={14}
          className={'text-slate-400 transition-transform ' + (open ? 'rotate-90' : '')}
        />
      </button>
      {open && (
        <div className="mt-4 space-y-4">
          <Separator />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Priority</Label>
              <Input
                type="number"
                value={form.priority}
                onChange={(e) =>
                  update({ priority: Number(e.target.value) || 0 })
                }
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Lower numbers run first. The first matching active trigger wins.
              </p>
            </div>
            <div>
              <Label>Active</Label>
              <Select
                value={form.is_active ? 'yes' : 'no'}
                onValueChange={(v: string) => update({ is_active: v === 'yes' })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Active</SelectItem>
                  <SelectItem value="no">Disabled</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Disabled triggers are skipped without firing.
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

/**
 * Per-recipient routing inputs. Renders the right fields based on the
 * spec's ``kind`` - consumer / contractor get a dotted-path input,
 * third_party gets two captured-values key inputs. Adding a new
 * recipient kind tomorrow means adding a new branch here.
 */
function RecipientFields({
  spec,
  onChange,
}: {
  spec: RecipientSpec
  onChange: (patch: Partial<RecipientSpec>) => void
}) {
  if (spec.kind === 'consumer' || spec.kind === 'contractor') {
    // No path input - the resource adapter decides where the
    // consumer/contractor lives on the workflow resource. Admins
    // pick the audience kind only.
    return null
  }

  if (spec.kind === 'third_party') {
    return (
      <div className="space-y-3">
        <div>
          <Label>Email captured-values key</Label>
          <Input
            className="mt-1 font-mono"
            value={spec.email_from_captured ?? ''}
            onChange={(e) => onChange({ email_from_captured: e.target.value })}
            placeholder="third_party_email"
          />
          <p className="text-xs text-slate-500 mt-1">
            Key in <span className="font-mono">captured_values</span> that
            holds the recipient's email. Use this for admin-supplied emails
            (yourself, an external lab, etc.) - admin types the email at
            action time and the value lands in this key.
          </p>
        </div>
        <div>
          <Label>Name captured-values key (optional)</Label>
          <Input
            className="mt-1 font-mono"
            value={spec.name_from_captured ?? ''}
            onChange={(e) => onChange({ name_from_captured: e.target.value })}
            placeholder="third_party_name"
          />
        </div>
      </div>
    )
  }

  return null
}
